import { useCallback, useEffect, useState } from 'react'
import { useLocation, useNavigate, useOutletContext } from 'react-router-dom'
import { Plus, RefreshCw } from 'lucide-react'
import {
  createChannel,
  deleteChannel,
  getAudioGroups,
  getChannels,
  getChannelStreamStatus,
  startChannelStream,
  stopChannelStream,
  updateChannel,
  updateChannelGroups,
  updateChannelLabelRules,
  uploadChannelCover,
  type AudioGroup,
  type Channel,
  type ChannelFilters,
  type StreamStatusResponse
} from '../api'
import {
  getLabels,
  type Label
} from '../api/labels'
import {
  getVisualGroups,
  type VisualGroup
} from '../api/visuals'
import { defaultChannelColorThemeId, type ChannelColorThemeId } from '../channelColorThemes'
import { DeleteConfirmDialog } from '../components/DeleteConfirmDialog'
import { useToast } from '../components/ToastProvider'
import { Button, adminMediaPlayerClassName } from '../components/ui'
import { AdminStreamPlayer } from '../features/channels/AdminStreamPlayer'
import { ChannelDialog } from '../features/channels/ChannelDialog'
import { ChannelList } from '../features/channels/ChannelList'
import type { AdminOutletContext } from '../layouts/AdminLayout'
import styles from './ChannelsPage.module.css'

const emptyChannelFilters: ChannelFilters = {}

const pc = adminMediaPlayerClassName

const createEmptyChannel = (): Channel => ({
  id: '',
  name: '',
  slug: '',
  description: null,
  cover_image_url: null,
  cover_derivatives: [],
  color_theme: defaultChannelColorThemeId,
  is_active: false,
  track_selection_mode: 'groups',
  include_match_mode: 'all',
  bpm_min: null,
  bpm_max: null,
  sort_order: 0,
  stream_bitrate: 256,
  stream_format: 'mp3',
  source_quality: 'mp3_only',
  created_at: '',
  updated_at: '',
  visual_groups: [],
  audio_groups: [],
  label_rules: []
})

export const ChannelsPage = () => {
  const { toast } = useToast()
  const location = useLocation()
  const navigate = useNavigate()
  const { closeMobileActions, setMobileMenuItems } = useOutletContext<AdminOutletContext>()
  const [audioGroups, setAudioGroups] = useState<AudioGroup[]>([])
  const [channels, setChannels] = useState<Channel[]>([])
  const [filters, setFilters] = useState<ChannelFilters>(emptyChannelFilters)
  const [labels, setLabels] = useState<Label[]>([])
  const [visualGroups, setVisualGroups] = useState<VisualGroup[]>([])
  const [selectedChannel, setSelectedChannel] = useState<Channel | null>(null)
  const [channelToDelete, setChannelToDelete] = useState<Channel | null>(null)
  const [deletingChannel, setDeletingChannel] = useState(false)
  const [streamStatuses, setStreamStatuses] = useState<Record<string, StreamStatusResponse | undefined>>({})
  const [streamStatusesLoading, setStreamStatusesLoading] = useState(false)
  const [startingStreamChannelId, setStartingStreamChannelId] = useState<string | null>(null)
  const [stoppingStreamChannelId, setStoppingStreamChannelId] = useState<string | null>(null)
  const [playingStream, setPlayingStream] = useState<{ channel: Channel; streamUrl: string } | null>(null)
  const [streamPlaybackCommand, setStreamPlaybackCommand] = useState<{ id: number; action: 'toggle' } | null>(null)
  const [streamPlaybackState, setStreamPlaybackState] = useState<{
    channelId: string | null
    loading: boolean
    playing: boolean
  }>({
    channelId: null,
    loading: false,
    playing: false
  })

  const openCreateChannelDialog = useCallback((): void => {
    setSelectedChannel(createEmptyChannel())
  }, [])

  const loadChannels = useCallback(async (nextFilters = filters): Promise<void> => {
    const payload = await getChannels(nextFilters)
    setChannels(payload)
  }, [filters])

  const loadSupportData = useCallback(async (): Promise<void> => {
    const [audioGroupsPayload, visualGroupsPayload, labelsPayload] = await Promise.all([
      getAudioGroups(),
      getVisualGroups(),
      getLabels({ status: 'active' })
    ])

    setAudioGroups(audioGroupsPayload)
    setVisualGroups(visualGroupsPayload)
    setLabels(labelsPayload)
  }, [])

  useEffect(() => {
    void Promise.all([
      loadChannels(),
      loadSupportData()
    ])
  }, [loadChannels, loadSupportData])

  useEffect(() => {
    const state = location.state as { editChannelId?: string } | null
    const channelToEdit = state?.editChannelId
      ? channels.find((channel) => channel.id === state.editChannelId)
      : null

    if (channelToEdit) {
      setSelectedChannel(channelToEdit)
      navigate('/admin/channels', { replace: true, state: null })
    }
  }, [channels, location.state, navigate])

  useEffect(() => {
    setMobileMenuItems(
      <button
        className="mobile-actions-item"
        onClick={() => {
          closeMobileActions()
          openCreateChannelDialog()
        }}
        role="menuitem"
        type="button"
      >
        <Plus size={17} />
        <span>Create channel</span>
      </button>
    )

    return () => setMobileMenuItems(null)
  }, [closeMobileActions, openCreateChannelDialog, setMobileMenuItems])

  const refresh = async (): Promise<void> => {
    await loadChannels()
    toast('Channels refreshed.')
  }

  const loadStreamStatuses = useCallback(async (options?: { showLoading?: boolean }): Promise<void> => {
    const channelsWithIds = channels.filter((channel) => channel.id)

    if (channelsWithIds.length === 0) {
      setStreamStatuses({})
      setStreamStatusesLoading(false)
      return
    }

    if (options?.showLoading) {
      setStreamStatusesLoading(true)
    }

    try {
      const statusEntries = await Promise.all(
        channelsWithIds.map(async (channel) => {
          try {
            const status = await getChannelStreamStatus(channel.id)

            return [channel.id, status] as const
          } catch {
            return [channel.id, undefined] as const
          }
        })
      )

      setStreamStatuses(Object.fromEntries(statusEntries))
    } finally {
      if (options?.showLoading) {
        setStreamStatusesLoading(false)
      }
    }
  }, [channels])

  useEffect(() => {
    void loadStreamStatuses({ showLoading: true })
  }, [loadStreamStatuses])

  useEffect(() => {
    if (channels.length === 0) {
      return undefined
    }

    const interval = setInterval(() => {
      void loadStreamStatuses()
    }, 10000)

    return () => clearInterval(interval)
  }, [channels, loadStreamStatuses])

  const handleFilter = async (nextFilters: ChannelFilters): Promise<void> => {
    setFilters(nextFilters)
    await loadChannels(nextFilters)
  }

  const handleToggleActive = async (channel: Channel, active: boolean): Promise<void> => {
    await updateChannel({ ...channelToInput(channel), is_active: active })
    toast(active ? `Activated "${channel.name}".` : `Deactivated "${channel.name}".`)
    await loadChannels()
  }

  const handleStartStream = async (channel: Channel): Promise<void> => {
    setStartingStreamChannelId(channel.id)

    try {
      await startChannelStream(channel.id)
      toast(`Started stream for "${channel.name}".`)
      await loadChannels(filters)
      await loadStreamStatuses()
      window.setTimeout(() => {
        void loadStreamStatuses()
      }, 1500)
    } finally {
      setStartingStreamChannelId(null)
    }
  }

  const handleStopStream = async (channel: Channel): Promise<void> => {
    setStoppingStreamChannelId(channel.id)

    try {
      await stopChannelStream(channel.id)
      toast(`Stopped stream for "${channel.name}".`)
      setPlayingStream((current) => current?.channel.id === channel.id ? null : current)
      setStreamPlaybackState((current) => current.channelId === channel.id
        ? { channelId: null, loading: false, playing: false }
        : current)
      await loadChannels(filters)
      await loadStreamStatuses()
    } finally {
      setStoppingStreamChannelId(null)
    }
  }

  const handleToggleStreamPlayback = (channel: Channel): void => {
    if (playingStream?.channel.id === channel.id) {
      setStreamPlaybackCommand((current) => ({
        action: 'toggle',
        id: (current?.id ?? 0) + 1
      }))
      return
    }

    const status = streamStatuses[channel.id]

    if (!status || status.status !== 'live') {
      toast(`Stream for "${channel.name}" is not live yet.`)
      return
    }

    setStreamPlaybackState({
      channelId: channel.id,
      loading: true,
      playing: false
    })
    setPlayingStream({
      channel,
      streamUrl: status.stream_url
    })
  }

  const closeStreamPlayer = useCallback((): void => {
    setPlayingStream(null)
    setStreamPlaybackState({
      channelId: null,
      loading: false,
      playing: false
    })
  }, [])

  const handleStreamPlaybackStateChange = useCallback((state: { loading: boolean; playing: boolean }): void => {
    setStreamPlaybackState((current) => {
      if (!playingStream) {
        return current.channelId === null && !current.loading && !current.playing
          ? current
          : { channelId: null, loading: false, playing: false }
      }

      const nextState = {
        channelId: playingStream.channel.id,
        loading: state.loading,
        playing: state.playing
      }

      return current.channelId === nextState.channelId
        && current.loading === nextState.loading
        && current.playing === nextState.playing
        ? current
        : nextState
    })
  }, [playingStream])

  const handleDelete = async (channel: Channel): Promise<void> => {
    setChannelToDelete(channel)
  }

  const confirmDelete = async (): Promise<void> => {
    if (!channelToDelete) {
      return
    }

    setDeletingChannel(true)
    try {
      await deleteChannel(channelToDelete.id)
      toast(`Deleted channel "${channelToDelete.name}".`)
      setChannelToDelete(null)
      await loadChannels()
    } finally {
      setDeletingChannel(false)
    }
  }

  const handleSave = async (input: {
    id: string
    name: string
    slug: string
    description: string | null
    color_theme: ChannelColorThemeId
    is_active: boolean
    sort_order: number
    track_selection_mode: Channel['track_selection_mode']
    include_match_mode: Channel['include_match_mode']
    bpm_min: number | null
    bpm_max: number | null
    stream_bitrate: number
    stream_format: Channel['stream_format']
    source_quality: Channel['source_quality']
    visualGroupIds: string[]
    audioGroupIds: string[]
    labelRules: Array<{ labelId: string; mode: 'include' | 'exclude' | 'boost'; weight?: number }>
    coverFile: File | null
  }): Promise<void> => {
    let savedChannel: Channel

    if (input.id) {
      savedChannel = await updateChannel({
        id: input.id,
        name: input.name,
        slug: input.slug,
        description: input.description,
        color_theme: input.color_theme,
        is_active: input.is_active,
        track_selection_mode: input.track_selection_mode,
        include_match_mode: input.include_match_mode,
        bpm_min: input.bpm_min,
        bpm_max: input.bpm_max,
        sort_order: input.sort_order,
        stream_bitrate: input.stream_bitrate,
        stream_format: input.stream_format,
        source_quality: input.source_quality
      })
      toast(`Updated channel "${input.name}".`)
    } else {
      savedChannel = await createChannel({
        name: input.name,
        slug: input.slug,
        description: input.description,
        color_theme: input.color_theme,
        is_active: input.is_active,
        track_selection_mode: input.track_selection_mode,
        include_match_mode: input.include_match_mode,
        bpm_min: input.bpm_min,
        bpm_max: input.bpm_max,
        sort_order: input.sort_order,
        stream_bitrate: input.stream_bitrate,
        stream_format: input.stream_format,
        source_quality: input.source_quality
      })
      toast(`Created channel "${input.name}".`)
    }

    await updateChannelGroups(savedChannel.id, input.visualGroupIds, input.audioGroupIds)
    await updateChannelLabelRules(savedChannel.id, input.labelRules)

    if (input.coverFile) {
      await uploadChannelCover(savedChannel.id, input.coverFile)
    }

    setSelectedChannel(null)
    await loadChannels()
  }

  return (
    <>
      <div className={['channels-page', styles.page].join(' ')}>
        <div className={['page-header channels-page-header', styles.header].join(' ')}>
          <div>
            <h1 className="page-title">Manage Channels</h1>
            <p className="page-subtitle">
              Create and configure playback channels. Bind visual and audio groups to define content.
            </p>
          </div>
          <div className="page-header-actions">
            <Button mobileFullWidth onClick={() => void refresh()}>
              <RefreshCw size={16} /> Refresh
            </Button>
          </div>
        </div>

        <ChannelList
          channels={channels}
          filters={filters}
          onCreate={openCreateChannelDialog}
          onDelete={handleDelete}
          onEdit={setSelectedChannel}
          onFilter={handleFilter}
          onToggleStreamPlayback={handleToggleStreamPlayback}
          onOpenPlayground={(channel) => navigate(`/admin/channels/${channel.id}/playground`)}
          onStartStream={handleStartStream}
          onStopStream={handleStopStream}
          onToggleActive={handleToggleActive}
          playingStreamChannelId={streamPlaybackState.channelId}
          startingStreamChannelId={startingStreamChannelId}
          stoppingStreamChannelId={stoppingStreamChannelId}
          streamPlaybackLoading={streamPlaybackState.loading}
          streamPlaybackPlaying={streamPlaybackState.playing}
          streamStatuses={streamStatuses}
          streamStatusesLoading={streamStatusesLoading}
        />
      </div>

      {selectedChannel && (
        <ChannelDialog
          audioGroups={audioGroups}
          channel={selectedChannel}
          labels={labels}
          visualGroups={visualGroups}
          onClose={() => setSelectedChannel(null)}
          onSave={handleSave}
        />
      )}

      {channelToDelete && (
        <DeleteConfirmDialog
          description="This will delete the channel, its group bindings, and its cover image when it is not used elsewhere. This action cannot be undone."
          isDeleting={deletingChannel}
          itemName={channelToDelete.name}
          onCancel={() => setChannelToDelete(null)}
          onConfirm={confirmDelete}
          title="Delete Channel"
        />
      )}

      <AdminStreamPlayer
        channel={playingStream?.channel ?? null}
        playbackCommand={streamPlaybackCommand}
        streamUrl={playingStream?.streamUrl ?? null}
        onClose={closeStreamPlayer}
        onPlaybackStateChange={handleStreamPlaybackStateChange}
      />
      {playingStream && <div className={pc('admin-audio-player-spacer')} />}
    </>
  )
}

const channelToInput = (channel: Channel) => ({
  id: channel.id,
  name: channel.name,
  slug: channel.slug,
  description: channel.description,
  color_theme: channel.color_theme,
  is_active: channel.is_active,
  sort_order: channel.sort_order,
  track_selection_mode: channel.track_selection_mode,
  include_match_mode: channel.include_match_mode,
  bpm_min: channel.bpm_min,
  bpm_max: channel.bpm_max,
  stream_bitrate: channel.stream_bitrate,
  stream_format: channel.stream_format,
  source_quality: channel.source_quality
})
